import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/icon";
import { fetchAllCategoryFields, usePath } from "@/contexts/path/path";
import { useAuthState, useIsOwner } from "@/contexts/auth/auth.provider";
import { CountersButtons } from "../../new-resolver/CountersButtons";
import css from "./.module.scss";
import Title from "../../Title";
import ListingImages from "../ListingImages";
import { AlertForUser, CLOSED_STATUS_MAP } from "./alert";
import { useMapListing } from "../../new-resolver/useFindListings";
import ListingVideo from "./listingVideo";
import { RandomAwarenessMessage } from "@/components/Listing/ListingScreen/awareness";
import {
  NextListingButton,
  PreviousListingButton,
} from "@/components/Listing/ListingScreen/generalControls";
import { RelatedListings } from "@/components/Listing/ListingScreen/relatedListings";
import { Prices } from "@/components/Listing/ListingScreen/prices";
import {
  listingToEECItemObject,
  trackViewItem,
} from "@/lib/tracking/trackEECEvents";
import { NewSpecsComponent } from "@/components/Listing/table/newSpecsComponent";
import NewListingOwner from "@/components/Listing/ListingScreen/owner/newListingOwner";
import FavButton from "@/components/Listing/ListingScreen/ListingControls/favButton";
import ShareButton from "@/components/Listing/ListingScreen/ListingControls/shareButton";
import { OwnerControls } from "@/components/Listing/ListingScreen/ownerControls";
import { TabsSpecs } from "@/components/Listing/table/tabsSpecs";
import { useQuery } from "@apollo/client";
import { GET_LISTING_EXTENDED_DETAILS } from "@/graphql/queries/listing";
import { ListingPricingComponent } from "@/components/Listing/ListingScreen/index";
import { ShowMore } from "@/components/Listing/showMore";
import { useDeviceViewPort } from "@/hooks/viewport";
import Tooltip from "@/components/Listing/ListingScreen/Tooltip";
import LikeButton from "@/components/Listing/ListingScreen/ListingControls/LikeButton";

export function FloatingListingDetails({
  listing: _listing,
  onListingChange,
  frozenContext,
} = {}) {
  const [listing] = useMapListing(_listing);

  const {
    updateContext,
    currentFrozenIndex = 0,
    categoriesDictionary,
    ...path
  } = usePath();

  const { price, imgs, title, status, published, is_auction } = listing;
  const isOpen = !CLOSED_STATUS_MAP[status];
  const { user } = useAuthState();

  const isOwner = useIsOwner(listing);

  const [fields, setFields] = useState({});
  const [showPrices, setShowPrices] = useState(false);
  const canViewContent = isOwner || published || user?.paid;
  const pricesRef = useRef(null);

  useEffect(() => {
    fetchAllCategoryFields(listing.category).then(setFields);
  }, [listing.category]);

  const { data } = useQuery(GET_LISTING_EXTENDED_DETAILS, {
    skip: !listing,
    variables: {
      id: listing.id,
    },
  });
  const [isMobile] = useDeviceViewPort();

  const extendedDetails = data?.Listing.getExtendedDetails || {};
  useEffect(() => {
    trackViewItem(
      listingToEECItemObject(_listing, undefined, categoriesDictionary),
      { value: price },
    );
  }, [_listing]);

  const hasPrices =
    listing?.price_12_payments ||
    listing?.price_2_payments ||
    listing?.price_4_payments;
  return (
    <div className={css.root + " " + css.white}>
      <Title />
      <div className={`${css.title} ${css.floating}`}>
        <h1>{title}</h1>
        <button onClick={() => onListingChange(undefined)} type="button">
          <Icon width={15} height={15} name="Close" />
        </button>
      </div>
      <div className={css.images}>
        <div className={css.controls}>
          {!isOwner && <LikeButton listing={listing} />}
          <FavButton listing={listing} />
          <ShareButton type="listing" listing={listing} />
          {isOwner ? <OwnerControls listing={listing} /> : null}
        </div>
        {isOpen && imgs && (
          <ListingImages listing={listing} loaded={listing.loadedImage} />
        )}
      </div>

      <div className={css.towColumns}>
        <div>
          <div className={css.categoryRow}>
            {!!path?.category && (
              <div className={css.auction}>
                {is_auction && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.04024 12.0766H7.70628L7.67673 11.9956C7.43584 11.3501 6.81817 10.9227 6.12661 10.9242H2.62738C1.93729 10.9242 1.31961 11.3516 1.07875 11.9971L1.04771 12.0781H0.701932C0.314773 12.0781 2.07811e-05 12.392 2.07811e-05 12.7781V13.207C-0.00293755 13.5931 0.310336 13.9085 0.697495 13.9099H0.701932L8.04024 13.9055C8.42738 13.9055 8.74215 13.5916 8.74215 13.2055V12.7767C8.74215 12.3905 8.42738 12.0766 8.04024 12.0766ZM13.555 11.3383L9.13669 6.60772C9.59331 6.18919 10.0041 5.72202 10.3602 5.21507C10.6735 5.35359 11.037 5.31233 11.3119 5.10748C11.7316 4.79506 11.8173 4.20116 11.504 3.78262L8.94902 0.379831C8.74363 0.104248 8.40523 -0.0372218 8.06387 0.00846275C7.54371 0.0762475 7.17872 0.552261 7.24817 1.071C5.51186 1.65164 4.00905 2.77314 2.95987 4.26894C2.64512 4.13042 2.2816 4.17168 2.00822 4.37653C1.58708 4.68896 1.50137 5.28286 1.81612 5.70139L4.36812 9.10418C4.57502 9.38271 4.91782 9.52566 5.26214 9.4785C5.78082 9.40628 6.14285 8.9288 6.07192 8.41007C6.65857 8.21406 7.22454 7.95321 7.75503 7.63489L11.0577 13.2026C11.1242 13.319 11.2055 13.428 11.3001 13.5238C11.9044 14.1457 12.9004 14.1605 13.5225 13.5562C14.1461 12.952 14.1609 11.9602 13.555 11.3383Z"
                      fill="#4D4D4D"
                    />
                  </svg>
                )}
                <h2 className={css.category}>
                  {is_auction ? "مزاد: " : ""}
                  {categoriesDictionary?.[listing?.category]?.name ||
                    path?.category?.name}
                </h2>
              </div>
            )}
            {listing?.precise_location_request_status === "approved" && (
              <Tooltip content="تم التحقق من دقة موقع العقار ">
                <div className={css.approvedPreciseLocation}>
                  <svg
                    width="10"
                    height="12"
                    viewBox="0 0 10 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 5.09185C10 5.55757 9.90885 6.04657 9.72656 6.55886C9.54427 7.07115 9.27865 7.60414 8.92969 8.15783C8.58073 8.71151 8.14583 9.28331 7.625 9.87322C7.10417 10.4631 6.50521 11.0686 5.82812 11.6895C5.71354 11.793 5.58333 11.8706 5.4375 11.9224C5.29167 11.9741 5.14583 12 5 12C4.85417 12 4.70833 11.9741 4.5625 11.9224C4.41667 11.8706 4.28646 11.793 4.17188 11.6895C3.49479 11.0686 2.89583 10.4631 2.375 9.87322C1.85417 9.28331 1.41927 8.71151 1.07031 8.15783C0.721354 7.60414 0.455729 7.07115 0.273438 6.55886C0.0911458 6.04657 0 5.55757 0 5.09185C0 3.53946 0.502604 2.30272 1.50781 1.38163C2.51302 0.460543 3.67708 0 5 0C6.32292 0 7.48698 0.460543 8.49219 1.38163C9.4974 2.30272 10 3.53946 10 5.09185Z"
                      fill="white"
                    />
                    <path
                      d="M4.25635 5.85246L3.74921 5.31621C3.60431 5.163 3.43527 5.08639 3.24207 5.08639C3.04887 5.08639 2.87982 5.163 2.73492 5.31621C2.59003 5.46942 2.51758 5.65136 2.51758 5.86203C2.51758 6.0727 2.59003 6.25464 2.73492 6.40786L3.74921 7.4995C3.89411 7.65272 4.06316 7.72932 4.25635 7.72932C4.44955 7.72932 4.6186 7.65272 4.7635 7.4995L7.33544 4.77996C7.48034 4.62675 7.55279 4.44481 7.55279 4.23414C7.55279 4.02347 7.48034 3.84153 7.33544 3.68832C7.19054 3.5351 7.01847 3.4585 6.81924 3.4585C6.62 3.4585 6.44794 3.5351 6.30304 3.68832L4.25635 5.85246Z"
                      fill="#52B44A"
                    />
                  </svg>
                  <span>موقع دقيق</span>
                </div>
              </Tooltip>
            )}
          </div>
          <ListingPricingComponent
            listing={listing}
            pricesRef={pricesRef}
            canViewContent={canViewContent}
          />

          {!published && (
            <AlertForUser
              user={user}
              alert_box={listing.alert_box}
              listingUserId={listing.user_id}
            />
          )}

          <NewSpecsComponent
            {...fields}
            listing={{
              ...listing,
              extended_details: {
                ...listing.extended_details,
                ...extendedDetails,
              },
            }}
          />

          {listing.content && canViewContent ? (
            <div className={`${css.card} ${css.flex} ${css.bottomBorderOnly}`}>
              <ShowMore lines={isMobile ? 2 : 10}>{listing.content}</ShowMore>
            </div>
          ) : null}
          {!!listing?.has_video && (
            <ListingVideo videoItem={(listing?.videos || [])[0]} />
          )}

          <Prices listing={listing} ref={pricesRef} />

          <TabsSpecs listing={listing} {...fields} />
          <div className={css.changeControlls}>
            <PreviousListingButton
              onListingChange={(e) => {
                onListingChange(e.listing);
                updateContext({
                  currentFrozenIndex: e.currentFrozenIndex,
                });
              }}
              frozenContext={frozenContext}
            >
              الإعلان السابق
            </PreviousListingButton>
            <NextListingButton
              onListingChange={(e) => {
                onListingChange(e.listing);
                updateContext({
                  currentFrozenIndex: e.currentFrozenIndex,
                });
              }}
              frozenContext={frozenContext}
            >
              الإعلان التالي
            </NextListingButton>
          </div>
        </div>

        <div>
          <div className={css.ownerWrapper}>
            {!isOwner && (published || user?.paid) && (
              <div>
                <div className={css.title}>معلومات المعلن</div>
                <div className={css.card}>
                  <NewListingOwner listing={listing} />
                </div>
              </div>
            )}
            <RandomAwarenessMessage
              listingId={listing.id}
              style={{ maxWidth: "40rem", fontSize: "10px" }}
            />
          </div>
        </div>
      </div>

      <CountersButtons bottom />

      <RelatedListings onListingChange={onListingChange} listing={listing} />
    </div>
  );
}

export default FloatingListingDetails;
